import type { ComponentProps } from "react";

import { Badge2 } from "@/ui2/atoms/Badge";
import { cn } from "@/lib/helpers/cn";
import type { Note } from "@/core/types";
import { testLocator } from "@/lib/helpers/test-locator";

type BadgeSize = ComponentProps<typeof Badge2>["size"];

type Props = {
  badgeSize?: BadgeSize;
  className?: string;
  note: Note;
};

export function NoteBadges({ badgeSize = "xs", className, note }: Props) {
  if (!note) return null;

  const content = getNoteBadgesContent({ badgeSize, note });

  if (!content) return null;

  return (
    <ul
      className={cn("tw:flex tw:items-start tw:justify-center tw:gap-1 tw:lg:flex-col", className)}
    >
      {content}
    </ul>
  );
}

export function getNoteBadgesContent({
  badgeSize = "xs",
  note,
}: {
  badgeSize?: BadgeSize;
  note: Note;
}) {
  const badges: React.ReactNode[] = [];

  if (!note.is_billable) {
    badges.push(<NonBillableBadge key="badge-non-billable" size={badgeSize} />);
  }

  if (note.content?.patient_no_show) {
    badges.push(<PatientNoShowBadge key="badge-patient-no-show-1" size={badgeSize} />);
  }

  if (
    note.note_type === "Initial Clinical Assessment" &&
    note.content?.patient_no_show_array &&
    !note.completed_date_time
  ) {
    badges.push(<PatientNoShowBadge key="badge-patient-no-show-2" size={badgeSize} />);
  }

  if (note.call_state === "cancelled") {
    badges.push(<SessionCancelledBadge key="badge-session-cancelled" size={badgeSize} />);
  }

  return badges.length > 0 ? badges : null;
}

function NonBillableBadge({ size }: { size: BadgeSize }) {
  return (
    <li className="tw:contents">
      <Badge2 size={size} variant="navy" {...testLocator("noteNonBillableBadge")}>
        Non-Billable
      </Badge2>
    </li>
  );
}

function PatientNoShowBadge({ size }: { size: BadgeSize }) {
  return (
    <li className="tw:contents">
      <Badge2 size={size} variant="warning" {...testLocator("notePatientNoShowBadge")}>
        Patient No-Show
      </Badge2>
    </li>
  );
}

function SessionCancelledBadge({ size }: { size: BadgeSize }) {
  return (
    <li className="tw:contents">
      <Badge2 size={size} variant="error">
        Session Cancelled
      </Badge2>
    </li>
  );
}
